
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import NavBar from '../../components/feature/NavBar';
import TabBar from '../../components/feature/TabBar';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface Whiteboard {
  id: string;
  title: string;
  shareCode: string;
  createdAt: string;
  lastModified: string;
  thumbnail?: string;
}

export default function WhiteboardList() {
  const navigate = useNavigate();
  const [whiteboards, setWhiteboards] = useState<Whiteboard[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [joinCode, setJoinCode] = useState('');
  
  // Force teacher mode for testing
  const user = JSON.parse(localStorage.getItem('user') || '{"name": "Teacher Demo", "isTeacher": true}');
  
  // Ensure user is set as teacher for testing
  useEffect(() => {
    if (!user.isTeacher) {
      localStorage.setItem('user', JSON.stringify({
        name: "Teacher Demo",
        isTeacher: true,
        email: "teacher@demo.com",
        id: "teacher-demo-1"
      }));
    }
  }, []);

  useEffect(() => {
    // Load whiteboards from localStorage
    const savedWhiteboards = localStorage.getItem('whiteboards');
    if (savedWhiteboards) {
      setWhiteboards(JSON.parse(savedWhiteboards));
    }
  }, []);

  const generateShareCode = () => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  const createWhiteboard = () => {
    if (!newTitle.trim()) return;
    
    const newWhiteboard: Whiteboard = {
      id: Date.now().toString(),
      title: newTitle,
      shareCode: generateShareCode(),
      createdAt: new Date().toISOString(),
      lastModified: new Date().toISOString()
    };
    
    const updatedWhiteboards = [...whiteboards, newWhiteboard];
    setWhiteboards(updatedWhiteboards);
    localStorage.setItem('whiteboards', JSON.stringify(updatedWhiteboards));
    
    setNewTitle('');
    setShowCreateModal(false);
    navigate(`/whiteboard/${newWhiteboard.id}`);
  };

  const joinWhiteboard = () => {
    if (!joinCode.trim()) return;
    
    // In a real app, this would check the server for the whiteboard
    // For demo purposes, we'll simulate finding a whiteboard
    const mockWhiteboard = {
      id: 'shared-' + joinCode,
      title: `Shared Board (${joinCode})`,
      shareCode: joinCode
    };
    
    setJoinCode('');
    setShowJoinModal(false);
    navigate(`/whiteboard/${mockWhiteboard.id}`);
  };

  const copyShareCode = async (shareCode: string) => {
    try {
      await navigator.clipboard.writeText(shareCode);
      // Show success feedback
      const button = event?.target as HTMLElement;
      if (button) {
        button.innerHTML = '<i class="ri-check-line"></i>';
        setTimeout(() => {
          button.innerHTML = '<i class="ri-clipboard-line"></i>';
        }, 2000);
      }
    } catch (err) {
      console.log('Failed to copy share code');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-50">
      <NavBar />
      <TabBar />
      
      <div className="pt-28 pb-20 md:pb-8">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">Whiteboards</h2>
            <p className="text-lg md:text-xl text-gray-600">Create and share interactive whiteboards for collaborative learning</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <Button onClick={() => setShowCreateModal(true)} className="flex items-center">
              <i className="ri-add-line mr-2"></i>
              Create Whiteboard
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowJoinModal(true)}
              className="flex items-center"
            >
              <i className="ri-login-box-line mr-2"></i>
              Join Whiteboard
            </Button>
          </div>

          {/* Whiteboards Grid */}
          {whiteboards.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {whiteboards.map((board) => (
                <Card 
                  key={board.id}
                  hover
                  onClick={() => navigate(`/whiteboard/${board.id}`)}
                  className="cursor-pointer"
                >
                  <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl mb-4 flex items-center justify-center">
                    <i className="ri-artboard-line text-4xl text-gray-400"></i>
                  </div>
                  <h3 className="font-bold text-gray-800 mb-2">{board.title}</h3>
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                    <span className="font-mono bg-gray-100 px-2 py-1 rounded">Share: {board.shareCode}</span>
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        copyShareCode(board.shareCode);
                      }}
                      className="text-[#2d5a35] hover:text-green-700 p-1 hover:bg-green-50 rounded"
                      title="Copy share code"
                    >
                      <i className="ri-clipboard-line"></i>
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Last modified: {new Date(board.lastModified).toLocaleDateString()}
                  </p>
                </Card>
              ))}
            </div>
          )}

          {/* Empty State for Teachers */}
          {whiteboards.length === 0 && (
            <Card className="text-center max-w-md mx-auto">
              <div className="w-20 h-20 bg-gradient-to-br from-[#2d5a35] to-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-artboard-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">Create Your First Whiteboard</h3>
              <p className="text-gray-600 mb-6 text-lg">Start collaborating with your students on an interactive whiteboard with drawing tools, stickers, and real-time sharing</p>
              <Button onClick={() => setShowCreateModal(true)} className="text-lg px-8 py-3">
                <i className="ri-add-line mr-2"></i>
                Create Whiteboard
              </Button>
            </Card>
          )}

          {/* Teacher Info Card */}
          <Card className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-[#2d5a35]">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-[#2d5a35] rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">How Whiteboards Work</h3>
                <div className="space-y-2 text-gray-600">
                  <p><strong>Create:</strong> Click "Create Whiteboard" to make a new collaborative canvas</p>
                  <p><strong>Share:</strong> Each whiteboard gets a unique 6-character share code</p>
                  <p><strong>Collaborate:</strong> Students enter the share code to join and draw together</p>
                  <p><strong>Features:</strong> Drawing tools, colors, stickers, emojis, and real-time collaboration</p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCreateModal(false)}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Create New Whiteboard</h3>
                <button 
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Whiteboard Name</label>
                <input
                  type="text"
                  placeholder="e.g., Math Lesson 1, Science Lab..."
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d5a35] focus:border-[#2d5a35]"
                  onKeyPress={(e) => e.key === 'Enter' && createWhiteboard()}
                  autoFocus
                />
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={createWhiteboard}
                  disabled={!newTitle.trim()}
                  className="flex-1"
                >
                  <i className="ri-add-line mr-2"></i>
                  Create
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}

      {/* Join Modal */}
      {showJoinModal && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowJoinModal(false)}></div>
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-800">Join Whiteboard</h3>
                <button 
                  onClick={() => setShowJoinModal(false)}
                  className="text-gray-400 hover:text-gray-600 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Share Code</label>
                <input
                  type="text"
                  placeholder="ABC123"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2d5a35] focus:border-[#2d5a35] text-center text-lg font-mono tracking-wider"
                  onKeyPress={(e) => e.key === 'Enter' && joinWhiteboard()}
                  maxLength={6}
                  autoFocus
                />
                <p className="text-sm text-gray-500 mt-2">Enter the 6-character code from your teacher</p>
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => setShowJoinModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={joinWhiteboard}
                  disabled={!joinCode.trim() || joinCode.length !== 6}
                  className="flex-1"
                >
                  <i className="ri-login-box-line mr-2"></i>
                  Join
                </Button>
              </div>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
